import { CardActions, CircularProgress, createStyles, makeStyles, Theme } from '@material-ui/core';
import CommentIconMUI from '@material-ui/icons/Comment';
import MuiCreateIcon from '@material-ui/icons/Create';
import MuiDeleteIcon from '@material-ui/icons/Delete';
import MoreVertIconMUI from '@material-ui/icons/MoreVert';
import VisibilityIconMUI from '@material-ui/icons/Visibility';
import classNames from 'classnames';
import React from 'react';

import { ReactComponent as PublishSvg } from '../../../../assets/publish.svg';
import { ReactComponent as ShareSvg } from '../../../../assets/share.svg';
import { ReactComponent as UnpublishSvg } from '../../../../assets/unpublish.svg';
import {
  FeedPostDetailsFragment,
  PublishStatus,
  useDeletePostMutation,
  usePublishPostMutation,
  useWatchPostMutation,
} from '../../../../graphql/gen/types';
import { useConfirmAction } from '../../../../providers/plex_provider';
import { colorPalette, spacings } from '../../../../theme';
import { ButtonColor, PlexButton } from '../../../shared/primitives/PlexButton';
import { IconButtonSize, PlexIconButton } from '../../../shared/primitives/PlexIconButton';
import { useSelectedPlexPost } from '../../details/post/useSelectedPlexPost';
import { useDialogState } from '../../dialog/useDialogState';
import { canDeletePost, canEditPost, canPublishPost, canUnpublishPost } from '../../permissions';
import { logAnalytics } from '../../shared/logAnalytics';
import { MoreOptionsMenu } from '../../shared/MoreOptionsMenu';
import { SharePopover } from '../SharePopover';
import { useSharePopover } from '../useSharePopover';

type Props = {
  plexPost: FeedPostDetailsFragment;
};

export function PlexPostFooter(props: Props): React.ReactElement {
  const { plexPost } = props;
  const [, setDialogState] = useDialogState();
  const [shareAnchorEl, handleClickShare, handleCloseShare] = useSharePopover();
  const { id, permalink, commentsCount, publishStatus } = plexPost;
  const [, setSelectedPlexPost] = useSelectedPlexPost();
  const [publishPost, { loading: publishingLoading }] = usePublishPostMutation();
  const [watchPost, { loading: watchLoading }] = useWatchPostMutation();
  const classes = useStyles();
  const [deletePost] = useDeletePostMutation();
  const confirmAction = useConfirmAction();

  const handleOpenComments = (): void => {
    setSelectedPlexPost({ permalink, commentsVisible: true });
  };

  const handlePublish = (): void => {
    publishPost({
      variables: {
        publishPostInput: {
          postId: plexPost.id,
          checked: true,
        },
      },
    });
    logAnalytics({ name: 'post published', details: { plexId: plexPost.plex.id, itemId: plexPost.id } });
  };

  const handleSetEditDialog = (): void => {
    setDialogState({ type: 'edit_post', postId: id });
  };

  const handleUnpublish = (): void => {
    publishPost({
      variables: {
        publishPostInput: {
          postId: plexPost.id,
          checked: false,
        },
      },
    });
    logAnalytics({ name: 'post unpublished', details: { plexId: plexPost.plex.id, itemId: plexPost.id } });
  };

  const handleDelete = (): void => {
    confirmAction({
      title: 'Confirmation',
      message: 'Are you sure you want to permanently delete this post?',
      confirmButton: {
        onClick: (): void => {
          deletePost({ variables: { postId: id } });
          logAnalytics({ name: 'post deleted', details: { plexId: plexPost.plex.id, itemId: plexPost.id } });
        },
        label: 'Delete',
        color: ButtonColor.RedBackground,
      },
    });
  };

  const handleWatch = (): void => {
    watchPost({
      variables: {
        payload: {
          postId: plexPost.id,
          watching: !plexPost.watching,
        },
      },
    });
    logAnalytics({
      name: plexPost.watching ? 'post watching removed' : 'post watching added',
      details: { plexId: plexPost.plex.id, itemId: plexPost.id },
    });
  };

  const plexRelation = plexPost.plex.currentUserRelation.state;

  return (
    <CardActions className={classes.actionsContainer}>
      <div className={classes.actionsLeftContainer}>
        {publishStatus === PublishStatus.Internal &&
          canPublishPost({
            plexRelation,
            status: plexPost.publishStatus,
          }) && (
            <PlexButton
              disabled={publishingLoading}
              className={classes.publishButton}
              onClick={handlePublish}
              color={ButtonColor.TransparentGray}
              SvgIconLeft={PublishSvg}
              label={
                <>
                  {publishingLoading && <CircularProgress size={24} className={classes.publishLoading} />}
                  <span>{publishingLoading ? 'Publishing...' : 'Publish'}</span>
                </>
              }
              tooltip={'Publish this post to your organization'}
            />
          )}
        <PlexButton
          onClick={handleOpenComments}
          label={`Comment (${commentsCount})`}
          MuiIconLeft={CommentIconMUI}
          color={ButtonColor.TransparentGray}
          tooltip={'Add a comment to this plex'}
        />
      </div>
      <div className={classes.actionsRightContainer}>
        <PlexIconButton
          onClick={handleWatch}
          disabled={watchLoading}
          MuiIcon={VisibilityIconMUI}
          color={plexPost.watching ? ButtonColor.TransparentGreen : ButtonColor.TransparentGray}
          tooltip={plexPost.watching ? 'Stop watching' : 'Watch'}
          size={IconButtonSize.Normal}
        />
        <PlexIconButton
          onClick={handleClickShare}
          SvgIcon={ShareSvg}
          color={ButtonColor.TransparentGray}
          tooltip={'Share this plex'}
          size={IconButtonSize.Normal}
        />
        <SharePopover itemId={id} itemType="post" anchorEl={shareAnchorEl} handleClose={handleCloseShare} />

        <MoreOptionsMenu
          id={'options_post'}
          Button={PlexIconButton}
          size={IconButtonSize.Normal}
          MuiIcon={MoreVertIconMUI}
          tooltip={'Open more options for this plex'}
          color={ButtonColor.TransparentGray}
          options={[
            canEditPost({ plexRelation, postRelation: plexPost.currentUserRelation })
              ? {
                  label: (
                    <>
                      <MuiCreateIcon className={classes.moreOptionIcon} />
                      <span>Edit</span>
                    </>
                  ),
                  onClick: handleSetEditDialog,
                }
              : null,
            canUnpublishPost({ plexRelation, status: plexPost.publishStatus })
              ? {
                  label: (
                    <>
                      <UnpublishSvg className={classes.moreOptionIcon} />
                      <span>Unpublish</span>
                    </>
                  ),
                  onClick: handleUnpublish,
                }
              : null,
            canDeletePost({ plexRelation, postRelation: plexPost.currentUserRelation })
              ? {
                  label: (
                    <>
                      <MuiDeleteIcon className={classNames(classes.moreOptionIcon, classes.deleteButton)} />
                      <span className={classes.deleteButton}>Delete</span>
                    </>
                  ),
                  onClick: handleDelete,
                }
              : null,
          ]}
          menuProps={{
            marginThreshold: 0,
            anchorOrigin: { horizontal: 'right', vertical: 'top' },
            transformOrigin: { horizontal: 'right', vertical: 'bottom' },
          }}
        />
      </div>
    </CardActions>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    actionsContainer: {
      justifyContent: 'space-between',
      padding: '12px 8px',
      borderWidth: 0,
      boxShadow: 'none',
    },
    publishButton: {
      marginRight: spacings.small,
    },
    actionsLeftContainer: {
      display: 'flex',
      flexDirection: 'row',
    },
    actionsRightContainer: {
      display: 'flex',
      flexDirection: 'row',
    },
    publishLoading: {
      marginRight: spacings.normal,
    },
    moreOptionIcon: {
      fill: `${colorPalette.purpleGray._500} !important`,
      marginRight: spacings.small,
      width: IconButtonSize.Small,
      height: IconButtonSize.Small,
    },
    deleteButton: {
      fill: `${colorPalette.red._400} !important`,
      color: colorPalette.red._400,
    },
  })
);
